import React from 'react';

import { classnames } from '@lumapps/classnames';
import { CustomContentType } from '@lumapps/content-types/types';
import { Group } from '@lumapps/groups/types';
import { Instance } from '@lumapps/instance/types';
import { GenericEntityBlock } from '@lumapps/lumx-blocks/components/GenericEntityBlock';
import { useDynamicIcon } from '@lumapps/lumx/hooks/useDynamicIcon';
import { mdiTextBoxOutline } from '@lumapps/lumx/icons';
import { Size } from '@lumapps/lumx/react';
import { getImageUrlFromContent, getMediaContent } from '@lumapps/medias/utils';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { Content } from '../../types';

export type ContentBlockProps = {
    /** Custom CSS class name. */
    className?: string;
    /** Content base data. */
    content: Partial<Pick<Content, 'id' | 'title' | 'mediaThumbnail' | 'instance' | 'customContentType' | 'feedKeys'>>;
    /** Provide custom content type icon to add as a badge on the thumbnail. */
    customContentType?: Pick<CustomContentType, 'icon'>;
    /** Provide content feed names to display in additional fields. */
    feeds?: Array<Pick<Group, 'name'>>;
    /** Provide instance name to display in additional fields. */
    instance?: Pick<Instance, 'name'>;
    /** Block variant. */
    variant?: 'small' | 'regular';
    /** whether the content is disabled or not */
    isDisabled?: boolean;
    /** When using navigationMapFormat, thumbnailUrl is set instead of content.mediaThumbnail */
    thumbnailUrl?: string;
    /** Optional override of the standard additional infos. */
    additionalInfoOverride?: React.ReactNode;
};

export const CLASSNAME = 'content-block';

/**
 * Content block displayed on the content picker.
 *
 * @family Blocks
 * @param ContentBlockProps
 * @returns ContentBlock
 */
export const ContentBlock = React.forwardRef<HTMLDivElement, ContentBlockProps>((props, ref) => {
    const {
        className,
        content,
        customContentType,
        feeds,
        instance,
        isDisabled,
        thumbnailUrl,
        additionalInfoOverride,
        variant,
    } = props;
    const { translate, translateAndReplace } = useTranslate();
    const translatedTitle = translate(content.title) as string;
    const isSmallVariant = variant === 'small';

    /* Thumbnail badge icon. */
    const customContentTypeIcon = customContentType?.icon;
    const dynamicIcon = useDynamicIcon({ icon: customContentTypeIcon || '' });
    const icon = customContentTypeIcon ? dynamicIcon.iconPath : mdiTextBoxOutline;

    /* Thumbnail image. */
    const mediaContent = getMediaContent(content.mediaThumbnail as any, translate);
    const imageURL = thumbnailUrl || getImageUrlFromContent(mediaContent);

    const additionalInfo = [];

    if (!additionalInfoOverride) {
        if (feeds?.length) {
            additionalInfo.push(
                <GenericEntityBlock.Attribute
                    key="feeds"
                    value={translateAndReplace(GLOBAL.FORMAT_COLON, {
                        key: translate(GLOBAL.VISIBILITY) || '',
                        value: feeds.map((feed) => translate(feed.name)).join(', '),
                    })}
                />,
            );
        }

        if (instance?.name) {
            additionalInfo.push(
                <GenericEntityBlock.Attribute
                    key="instance"
                    value={translateAndReplace(GLOBAL.FORMAT_COLON, {
                        key: translate(GLOBAL.SOURCE) || '',
                        value: instance.name,
                    })}
                />,
            );
        }
    }

    return (
        <GenericEntityBlock
            title={translatedTitle}
            ref={ref}
            className={classnames(className, CLASSNAME)}
            isDisabled={isDisabled}
            shouldCenterTitle
            additionalInfo={additionalInfoOverride || additionalInfo}
            thumbnail={{
                image: imageURL,
                icon,
                alt: mediaContent?.name || translatedTitle,
                size: isSmallVariant ? Size.m : Size.l,
            }}
        />
    );
});
ContentBlock.displayName = 'ContentBlock';
